import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, User, Building2, Clock, AlertCircle } from "lucide-react";
import { Task } from "@/types/task";
import { TaskCountdown } from "./TaskCountdown";
import { TaskPriorityBadge, TaskStatusBadge } from "./TaskBadges";

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
}

export const TaskCard = ({ task, onClick }: TaskCardProps) => {
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <Card 
      className={`transition-shadow hover:shadow-md cursor-pointer ${isOverdue ? 'border-red-200' : ''}`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-right truncate">{task.title}</h3>
            {task.description && (
              <p className="text-sm text-gray-600 text-right mt-1 line-clamp-2">
                {task.description}
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <TaskPriorityBadge priority={task.priority} />
            <TaskStatusBadge status={task.status} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* معلومات التخصيص */}
        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
          {task.assigned_to && (
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>مُخصص إلى: {task.assigned_employee?.name || 'غير محدد'}</span>
            </div>
          )}
          
          {task.client_id && task.clients && (
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>عميل: {task.clients.name_ar}</span>
            </div>
          )}

          {task.company_id && task.companies && (
            <div className="flex items-center gap-1">
              <Building2 className="w-4 h-4" />
              <span>شركة: {task.companies.name_ar}</span>
            </div>
          )}
        </div>

        {/* التواريخ والوقت */}
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex flex-wrap items-center gap-4">
            {task.due_date && (
              <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : 'text-gray-600'}`}>
                <CalendarDays className="w-4 h-4" />
                <span>
                  موعد الإنجاز: {new Date(task.due_date).toLocaleDateString('ar-SA')}
                  {isOverdue && <AlertCircle className="w-4 h-4 mr-1 inline" />}
                </span>
              </div>
            )}
            
            {task.estimated_hours && (
              <div className="flex items-center gap-1 text-gray-600">
                <Clock className="w-4 h-4" />
                <span>ساعات متوقعة: {task.estimated_hours}</span>
              </div>
            )}
          </div>

          {task.status !== 'completed' && <TaskCountdown dueDate={task.due_date} />}
        </div>

        {/* التصنيف والعلامات */}
        <div className="flex flex-wrap gap-2">
          {task.task_categories && (
            <Badge variant="outline" style={{ backgroundColor: task.task_categories.color + '20', color: task.task_categories.color }}>
              {task.task_categories.name_ar}
            </Badge>
          )}
          
          {task.tags && task.tags.map((tag, index) => (
            <Badge key={index} variant="secondary" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
